/**
 * Pure math behind the animated peon's speaking mouth: turns a decoded audio
 * clip into a low-resolution "mouth envelope" (closed/half/open per frame),
 * and looks up which frame an <audio> element's current playback time falls
 * in. Shared because scripts/encode-audio.mjs (Node, build time) computes
 * the envelope from ffmpeg's decoded PCM and writes it to each voice
 * directory's envelopes.json, while src/components/LivePeon.tsx (the
 * browser, at playback time) reads the same file back and walks it with the
 * same frame rate. Never duplicate this formula in either place.
 */

/** Frames per second the envelope is sampled at (also the RMS window size: 1000 / FPS ms). */
export const ENVELOPE_FPS = 20;
const WINDOW_MS = 1000 / ENVELOPE_FPS;

/** Mouth state indices, matching public/ui/peon-sheet.json's `mouths` order. */
export const MOUTH_CLOSED = 0;
export const MOUTH_HALF = 1;
export const MOUTH_OPEN = 2;

// Relative to the clip's own loudest window (1.0), not any absolute volume,
// since the voice lines vary a lot in recording level. Picked so a quiet
// intake breath at the start or end of a line (a real feature of these
// recordings) stays closed, ordinary speech reads as half-open, and only the
// loudest syllables snap the mouth fully open.
const CLOSED_THRESHOLD = 0.2;
const OPEN_THRESHOLD = 0.55;

/**
 * RMS-per-window envelope of a mono PCM clip, mapped to mouth states.
 * `samples` are normalized floats in [-1, 1] (as decoded, before any
 * clipping) and `sampleRate` is samples per second. An empty or effectively
 * silent clip (max RMS ~0) comes back all closed instead of dividing by
 * zero or amplifying noise floor into a jittery mouth.
 */
export function computeEnvelopeFromPcm(samples: ArrayLike<number>, sampleRate: number): number[] {
  if (samples.length === 0 || sampleRate <= 0) return [];
  const windowSize = Math.max(1, Math.round((sampleRate * WINDOW_MS) / 1000));
  const windowCount = Math.ceil(samples.length / windowSize);
  const rms: number[] = new Array(windowCount).fill(0);
  let maxRms = 0;
  for (let w = 0; w < windowCount; w++) {
    const start = w * windowSize;
    const end = Math.min(start + windowSize, samples.length);
    let sumSquares = 0;
    for (let i = start; i < end; i++) {
      const s = samples[i];
      sumSquares += s * s;
    }
    const value = end > start ? Math.sqrt(sumSquares / (end - start)) : 0;
    rms[w] = value;
    if (value > maxRms) maxRms = value;
  }
  if (maxRms <= 0) return rms.map(() => MOUTH_CLOSED);
  return rms.map((v) => {
    const relative = v / maxRms;
    if (relative < CLOSED_THRESHOLD) return MOUTH_CLOSED;
    if (relative < OPEN_THRESHOLD) return MOUTH_HALF;
    return MOUTH_OPEN;
  });
}

/**
 * Which mouth state an envelope says to show at `currentTime` seconds into
 * playback. An empty envelope (not computed, or not found for this file)
 * reads as closed; a time past the last frame (the audio element reporting
 * a hair later than the decoded envelope covers) clamps to the last frame
 * rather than going out of bounds.
 */
export function envelopeStateAt(envelope: readonly number[], currentTime: number): number {
  if (envelope.length === 0) return MOUTH_CLOSED;
  const frame = Math.floor(Math.max(0, currentTime) * ENVELOPE_FPS);
  const index = Math.min(frame, envelope.length - 1);
  return envelope[index];
}
